import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMemo } from 'react';

type ViewFileModalProps = {
    open: boolean;
    onClose: () => void;
    filePath: string | null;
    title?: string;
    downloadFileName?: string;
};

export default function ViewFileModal({
    open,
    onClose,
    filePath,
    title = 'View File',
    downloadFileName,
}: ViewFileModalProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fileType = useMemo(() => {
        if (!filePath) return 'unknown';

        const cleanPath = filePath.split('?')[0].toLowerCase();

        if (cleanPath.endsWith('.pdf')) return 'pdf';
        if (
            cleanPath.endsWith('.jpg') ||
            cleanPath.endsWith('.jpeg') ||
            cleanPath.endsWith('.png') ||
            cleanPath.endsWith('.webp')
        ) {
            return 'image';
        }

        return 'unknown';
    }, [filePath]);

    const handleDownload = async () => {
        if (!filePath) return;

        try {
            const response = await fetch(filePath);
            if (!response.ok) throw new Error('Failed to download file');

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download =
                downloadFileName ||
                filePath.split('/').pop() ||
                'downloaded-file';

            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Error downloading file:', error);
            alert('Unable to download file.');
        }
    };

    const handleOpenInNewTab = () => {
        if (!filePath) return;
        window.open(filePath, '_blank', 'noopener,noreferrer');
    };

    const renderContent = () => {
        if (!filePath) {
            return (
                <Box
                    sx={{
                        minHeight: 260,
                        display: 'grid',
                        placeItems: 'center',
                    }}
                >
                    <Typography color="text.secondary">No file selected.</Typography>
                </Box>
            );
        }

        if (fileType === 'image') {
            return (
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        overflow: 'auto',
                        bgcolor: '#f8fafc',
                        borderRadius: 2,
                        p: 1,
                    }}
                >
                    <Box
                        component="img"
                        src={filePath}
                        alt="Preview"
                        sx={{
                            maxWidth: '100%',
                            maxHeight: '70dvh',
                            objectFit: 'contain',
                            borderRadius: 1,
                        }}
                    />
                </Box>
            );
        }

        if (fileType === 'pdf') {
            return (
                <Box
                    sx={{
                        width: '100%',
                        height: { xs: '70dvh', md: '75dvh' },
                        borderRadius: 2,
                        overflow: 'hidden',
                        bgcolor: '#f8fafc',
                    }}
                >
                    <iframe
                        src={filePath}
                        title="PDF Preview"
                        width="100%"
                        height="100%"
                        style={{ border: 'none' }}
                    />
                </Box>
            );
        }

        return (
            <Box
                sx={{
                    minHeight: 260,
                    display: 'grid',
                    placeItems: 'center',
                    textAlign: 'center',
                    gap: 1,
                }}
            >
                <InsertDriveFileOutlinedIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                <Typography variant="body1">Preview not available for this file type.</Typography>
                <Typography variant="body2" color="text.secondary">
                    You can still download or open it in a new tab.
                </Typography>
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 3,
                    overflow: 'hidden',
                },
            }}
        >
            <DialogTitle>{title}</DialogTitle>

            <DialogContent dividers>{renderContent()}</DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                }}
            >
                <Button variant="outlined" onClick={onClose}>
                    Close
                </Button>

                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        startIcon={<OpenInNewRoundedIcon />}
                        onClick={handleOpenInNewTab}
                        disabled={!filePath}
                    >
                        Open
                    </Button>

                    <Button
                        variant="contained"
                        startIcon={<DownloadRoundedIcon />}
                        onClick={handleDownload}
                        disabled={!filePath}
                    >
                        Download
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
}